import React, { useMemo } from 'react';
import type { Tag } from '@/shared/types/tag';
import {
  Card,
  Chip,
  Select,
  SelectItem,
  type SharedSelection,
} from '@/components/ui';
import { useTags } from '@/store/tags/hooks';

interface TagListProps {
  tags: Tag[];
  className?: string;
}

interface TagItemProps {
  tag: Tag;
  isRemovable?: boolean;
  onRemove?: () => void;
}

interface TagSelectProps {
  value?: string;
  onSingleChange?: (tagId: string | undefined) => void;
  onMultiChange?: (tagIds: string[]) => void;
  onSelectionChange?: (tagIds: string | string[] | undefined) => void;
  multiple?: boolean;
  className?: string;
  label?: string;
  placeholder?: string;
  startContent?: React.ReactNode;
}

export const TagItem: React.FC<TagItemProps> = ({
  tag,
  isRemovable,
  onRemove,
}) => {
  return (
    <Chip
      size='sm'
      className='rounded-full px-3 py-1 text-xs font-medium'
      isCloseable={isRemovable}
      onClose={onRemove}
      style={{
        background: tag.data.background,
        color: tag.data.foreground,
      }}
    >
      {tag.data.label}
    </Chip>
  );
};

export const TagList: React.FC<TagListProps> = ({ tags, className }) => (
  <div className={`flex flex-wrap gap-2 ${className}`}>
    {tags.map((tag) => (
      <TagItem key={tag.id} tag={tag} />
    ))}
  </div>
);

export const TagSelect: React.FC<TagSelectProps> = ({
  value,
  onSingleChange,
  onMultiChange,
  onSelectionChange,
  multiple,
  className,
  label,
  placeholder,
  startContent,
}) => {
  const { list: allTags } = useTags();
  const sortedTags = useMemo(() => {
    return allTags
      .slice(0)
      .sort((a, b) => a.data.label.localeCompare(b.data.label));
  }, [allTags]);
  const selectedKeys = value ? [value] : [];

  const selectionHandler = (selection: SharedSelection) => {
    const values = Array.from(selection);
    if (multiple) {
      if (onMultiChange) {
        onMultiChange(values as string[]);
      }

      onSelectionChange?.(values as string[]);
    } else {
      if (onSingleChange) {
        onSingleChange(values.length ? (values[0] as string) : undefined);
      }

      onSelectionChange?.(values.length ? (values[0] as string) : undefined);
    }
  };

  return (
    <Select
      items={sortedTags}
      selectedKeys={selectedKeys}
      onSelectionChange={selectionHandler}
      label={label}
      className={className}
      placeholder={placeholder}
      startContent={startContent}
      renderValue={(items) =>
        items.map((item) => item.data?.data.label).join(', ')
      }
    >
      {(item) => (
        <SelectItem key={item.id} textValue={item.data.label}>
          {item.data.label}
        </SelectItem>
      )}
    </Select>
  );
};

export const TagsSelect = ({
  value,
  tags,
  label,
  onTagSelected,
  className,
}: {
  value: string[];
  tags: Tag[];
  label?: string;
  onTagSelected?: (id: string, selected: boolean) => void;
  className?: string;
}) => {
  const selected = tags.filter((item) => value.includes(item.id));

  const onSelected = (id: string | undefined) => {
    if (!id) {
      return;
    }

    onTagSelected?.(id, true);
  };

  const onDeselected = (id: string | undefined) => {
    if (!id) {
      return;
    }

    onTagSelected?.(id, false);
  };

  return (
    <Card
      className={`border-1 border-gray-200 px-3 py-2 ${className}`}
      shadow='none'
    >
      {label && (
        <label className='text-[75%] font-medium text-foreground-600'>
          {label}
        </label>
      )}
      {selected.map((tag) => (
        <TagItem
          tag={tag}
          key={tag.id}
          isRemovable
          onRemove={() => onDeselected(tag.id)}
        />
      ))}
      <TagSelect onSingleChange={onSelected} />
    </Card>
  );
};
